import React, { useState, useRef } from 'react';
import { Box, TextField, IconButton, Divider, FormHelperText } from '@mui/material';
import { AddCircleRounded } from '@mui/icons-material';
import { useFormContext, useWatch } from 'react-hook-form';
import MultiSelectOptionEdit from './MultiSelectOptionEdit';
import MultiSelectOptionField from './MultiSelectOptionField';

import type { MultiselectQuestion, OptionsFieldError } from '../../types';

interface MultiSelectOptionsProps {
    fieldIndex?: number;
    error?: OptionsFieldError;
}

/**
 * Editable multi-select question answer options list.
 */
const MultiSelectOptions: React.FC<MultiSelectOptionsProps> = ({ fieldIndex, error }) => {
    const [newOption, setNewOption] = useState('');
    const optionIdsRef = useRef<string[]>([]);
    const nextIdRef = useRef(0);
    const formContext = useFormContext();

    // Watch the current question from form state
    const question = useWatch({
        control: formContext.control,
        name: `questions.${fieldIndex}` as const,
    }) as MultiselectQuestion;

    const questionOptions = question?.options || [];

    // Initialize option IDs if they don't exist for handling re-rendering of the component
    if (optionIdsRef.current.length !== questionOptions.length) {
        optionIdsRef.current = questionOptions.map((_, i) => optionIdsRef.current[i] || `option-${nextIdRef.current++}`);
    }

    const handleAddOption = () => {
        if (newOption.trim()) {
            const updatedQuestion = {
                ...question,
                options: [...questionOptions, newOption.trim()],
            };

            formContext.setValue(`questions.${fieldIndex}` as const, updatedQuestion, {
                shouldValidate: false,
                shouldDirty: true,
            });

            optionIdsRef.current.push(`option-${nextIdRef.current++}`);
            setNewOption('');
        }
    };

    const handleRemoveOption = (optionIndex: number) => {
        const newOptions = [...questionOptions];
        newOptions.splice(optionIndex, 1);
        optionIdsRef.current.splice(optionIndex, 1);

        const updatedQuestion = {
            ...question,
            options: newOptions,
        };

        formContext.setValue(`questions.${fieldIndex}` as const, updatedQuestion, {
            shouldValidate: false,
            shouldDirty: true,
        });
    };

    const handleUpdateOption = (optionIndex: number, newValue: string) => {
        const newOptions = [...questionOptions];
        newOptions[optionIndex] = newValue;

        const updatedQuestion = {
            ...question,
            options: newOptions,
        };

        formContext.setValue(`questions.${fieldIndex}` as const, updatedQuestion, {
            shouldValidate: false,
            shouldDirty: true,
        });
    };

    const hasError = !!error;
    const questionOptionsAmount = questionOptions.length;

    return (
        <Box sx={{ mt: 1, maxWidth: { xs: '100%', lg: '65%' } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <MultiSelectOptionField>
                    <TextField
                        fullWidth
                        size="small"
                        label="Answer option"
                        placeholder="New option"
                        value={newOption}
                        sx={{ width: '100%' }}
                        onChange={
                            (e) => setNewOption(e.target.value)
                            // call validation
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleAddOption();
                            }
                        }}
                        error={hasError && questionOptionsAmount < 2}
                    />
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={handleAddOption}
                        disabled={!newOption.trim()}
                        sx={{ mt: 1.35, ml: 0.5 }}
                    >
                        <AddCircleRounded fontSize="small" />
                    </IconButton>
                </MultiSelectOptionField>
                {hasError && (
                    <FormHelperText error sx={{ alignSelf: 'flex-start', ml: 0, mt: 0 }}>
                        {error?.options?.message}
                    </FormHelperText>
                )}
                {questionOptionsAmount > 0 && <Divider sx={{ borderColor: 'gray.30', width: '100%', mt: 0.75, mb: 1 }} />}
                {questionOptions.map((option, optionIndex) => (
                    <MultiSelectOptionEdit
                        key={optionIdsRef.current[optionIndex] || `fallback-${optionIndex}`}
                        value={option}
                        index={optionIndex}
                        onChange={handleUpdateOption}
                        onDelete={handleRemoveOption}
                        error={error?.options?.[optionIndex]?.message}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default MultiSelectOptions;
